// src/hooks/useCollabSession.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useRealtime } from '../contexts/RealtimeContext.js';

export default function useCollabSession(canvasContent, setCanvasContent) {
  const { username: ownerName, chatId } = useParams();
  const location = useLocation();
  const socket   = useRealtime();
  const sessionRoom = `${ownerName}/${chatId}`;

  // Controller tracking
  const [controllerId, setControllerId] = useState(null);
  const myId         = socket.id;
  const isController = controllerId === myId;

  // Collaboration session toggle & timer
  const initialCollab = location.pathname.startsWith('/s/');
  const [collabSessionActive, setCollabSessionActive]   = useState(initialCollab);
  const [collabSessionStartTime, setCollabSessionStartTime] = useState(null);
  const [collabSessionElapsed, setCollabSessionElapsed] = useState('00:00:00');

  // Prevent echo of remote updates
  const suppressNextEmit = useRef(false);

  // Join room when session starts
  useEffect(() => {
    if (!chatId || !collabSessionActive) return;
    socket.emit('join', { room: sessionRoom });
  }, [socket, chatId, collabSessionActive, sessionRoom]);

  // Handle incoming session_start
  useEffect(() => {
    function onSessionStart({ room, startTime }) {
      if (room !== sessionRoom) return;
      setCollabSessionStartTime(new Date(startTime));
    }
    socket.on('session_start', onSessionStart);
    return () => socket.off('session_start', onSessionStart);
  }, [socket, sessionRoom]);

  // Emit session_start once by controller
  useEffect(() => {
    if (!collabSessionActive || !isController || collabSessionStartTime) return;
    const iso = new Date().toISOString();
    socket.emit('session_start', { room: sessionRoom, startTime: iso });
    setCollabSessionStartTime(new Date(iso));
  }, [socket, collabSessionActive, isController, collabSessionStartTime, sessionRoom]);

  // Tick elapsed timer
  useEffect(() => {
    if (!collabSessionStartTime) return;
    const iv = setInterval(() => {
      const diff = Date.now() - collabSessionStartTime.getTime();
      const hh = String(Math.floor(diff / 3600000)).padStart(2,'0');
      const mm = String(Math.floor((diff % 3600000) / 60000)).padStart(2,'0');
      const ss = String(Math.floor((diff % 60000) / 1000)).padStart(2,'0');
      setCollabSessionElapsed(`${hh}:${mm}:${ss}`);
    }, 1000);
    return () => clearInterval(iv);
  }, [collabSessionStartTime]);

  // Controller-change listener
  useEffect(() => {
    function onControllerChange({ room, controllerId }) {
      if (room !== sessionRoom) return;
      setControllerId(controllerId);
    }
    socket.on('controller_change', onControllerChange);
    return () => socket.off('controller_change', onControllerChange);
  }, [socket, sessionRoom]);

  // takeControl callback
  const takeControl = useCallback(() => {
    socket.emit('set_controller', {
      room:         sessionRoom,
      controllerId: myId,
    });
    // broadcast current canvas immediately
    if (canvasContent) {
      socket.emit('canvas_update', {
        room:     sessionRoom,
        content:  canvasContent,
        clientId: socket.id
      });
    }
  }, [socket, canvasContent, myId, sessionRoom]);

  // When I am controller, emit any local canvas changes
  useEffect(() => {
    if (isController && canvasContent) {
      socket.emit('canvas_update', {
        room:     sessionRoom,
        content:  canvasContent,
        clientId: socket.id
      });
    }
  }, [isController, canvasContent, socket, sessionRoom]);

  // Live canvas emit, suppressing echoes
  useEffect(() => {
    if (suppressNextEmit.current) {
      suppressNextEmit.current = false;
      return;
    }
    if (!sessionRoom || !canvasContent || !isController) return;
    socket.emit('canvas_update', {
      room:     sessionRoom,
      content:  canvasContent,
      clientId: socket.id
    });
  }, [socket, canvasContent, isController, sessionRoom]);

  // Listen for remote canvas_update
  useEffect(() => {
    function onCanvasUpdate({ room, content, clientId }) {
      if (room !== sessionRoom) return;
      if (clientId === socket.id) return;
      if (isController) return;
      suppressNextEmit.current = true;
      setCanvasContent(content);
    }
    socket.on('canvas_update', onCanvasUpdate);
    return () => socket.off('canvas_update', onCanvasUpdate);
  }, [socket, sessionRoom, isController, setCanvasContent]);

  return {
    takeControl,
    controllerId,
    isController,
    collabSessionActive,
    setCollabSessionActive,
    collabSessionElapsed,
    sessionRoom,
    socket,
    suppressNextEmit,
  };
}
